import { useState, type ComponentType } from "react";
import {
  ArrowRight,
  Gauge,
  Lock,
  Server,
  ShieldCheck,
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useBookAssessment } from "../hooks/useBookAssessment";
import Seo from "../components/Seo";
import { buildServiceSchema } from "../utils/structuredData";
import Reveal from "../components/Reveal";
import { coreServices, whyChoosePillars } from "../content/services";

// The deep-navy "Security posture" band. Speaks to regulated buyers and reinforces
// the services model: we deploy privately and run it for you.
const securityPillars: { icon: ComponentType<{ className?: string }>; title: string; detail: string }[] = [
  {
    icon: Lock,
    title: "Your data stays yours",
    detail: "Deployed in your private cloud or on-prem. Sensitive records never touch a third party.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance-ready",
    detail: "Built to operate within the HIPAA, FERPA, OPRA, and privilege rules you already work under.",
  },
  {
    icon: Server,
    title: "Run as a managed service",
    detail: "Our engineers deploy, operate, and maintain it — not one more tool for your team to manage.",
  },
  {
    icon: Gauge,
    title: "Predictable, not metered",
    detail: "Fixed engagements instead of per-seat, per-query AI bills that climb every month.",
  },
];

const Home: React.FC = () => {
  const location = useLocation();
  const bookAssessment = useBookAssessment();
  const [activeService, setActiveService] = useState(0);
  const ActiveServiceIcon = coreServices[activeService].icon;

  const seoTitle = "Secure AI Automation Services | Contextral";
  const seoDescription = "Contextral delivers secure AI automation services — private workflow automation, document processing, and internal knowledge search built around the systems you already run.";
  const serviceSchema = buildServiceSchema(location.pathname, "Secure AI Automation Services", seoDescription);

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} jsonLd={serviceSchema} />
      <section id="hero" className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden bg-transparent py-14 sm:py-16 mb-0 max-w-full">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight text-text-primary animate-fade-in-up">
              <span>Secure AI Automation,<br /> Built For <span className="text-accentPurple">Your Infrastructure</span></span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed whitespace-pre-line text-text-secondary animate-fade-in-up delay-200 px-2">
              We automate workflows for regulated organizations who can't risk exposing sensitive data to third-party AI providers. We deploy and run private AI solutions inside the systems your team already uses, so you can focus on work that matters most.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 mb-6 sm:mb-8">
              {['Microsoft 365', 'Google Workspace', 'AWS', 'Oracle', 'Salesforce', 'Slack'].map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center rounded-full border border-borderSubtle bg-surface px-4 py-1.5 text-sm sm:text-base font-semibold text-text-secondary shadow-sm transition-colors duration-200 hover:border-primary/50 hover:text-primary"
                >
                  {name}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-fade-in-up delay-400">
              <button
                type="button"
                onClick={() => bookAssessment()}
                className="group bg-primary text-text-inverse px-8 py-4 rounded-full text-lg font-semibold hover:bg-primaryHover transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-primary/30 flex items-center"
              >
                Book a Free Assessment
                <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <NavLink
                to="/about"
                className="group text-primary px-8 py-4 rounded-full text-lg font-semibold border-2 border-primary/40 hover:border-primary/70 transition-all duration-300 flex items-center"
              >
                Explore Our Services
                <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="relative overflow-hidden py-12 sm:py-16 md:py-24 bg-surfaceSoft mb-0 max-w-full">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.04] via-transparent to-accentPurple/[0.05]" />
          <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-28 -right-16 h-96 w-96 rounded-full bg-accentPurple/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary mb-3 sm:mb-4">
              Our Core Services
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto px-2">
              Automation, intelligence, and integration delivered as a service, not a product you have to run.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-stretch">
            <div className="flex flex-col gap-2 sm:gap-3">
              {coreServices.map((service, index) => {
                const isActive = index === activeService;
                return (
                  <button
                    key={service.title}
                    type="button"
                    onClick={() => setActiveService(index)}
                    aria-expanded={isActive}
                    className={`text-left rounded-xl border p-3 sm:p-4 transition-all duration-300 ${
                      isActive
                        ? "border-primary bg-surfaceSoft shadow-md shadow-primary/10"
                        : "border-borderSubtle bg-surface hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-primary/20 bg-gradient-to-br from-primary/15 to-accentPurple/20 text-primary">
                        <service.icon aria-hidden="true" className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-text-primary">{service.title}</h3>
                    </div>
                    {isActive && (
                      <p className="text-text-secondary text-sm mt-2 sm:mt-3">{service.description}</p>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="relative min-h-[320px] overflow-hidden rounded-2xl border border-borderSubtle bg-gradient-to-br from-surfaceSoft via-surface to-primary/10 p-8 flex items-center justify-center">
              <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-accentPurple/15 blur-3xl" aria-hidden="true" />
              <div className="relative text-center max-w-md">
                <div className="mx-auto mb-6 inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-primary text-white shadow-xl shadow-primary/25">
                  <ActiveServiceIcon aria-hidden="true" className="h-12 w-12" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-text-primary mb-3">{coreServices[activeService].title}</h3>
                <p className="text-text-secondary leading-relaxed">{coreServices[activeService].description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-fade-in-up delay-400 mt-10">
            <NavLink
              to="/industry-examples"
              className="group bg-primary text-text-inverse px-8 py-4 rounded-full text-lg font-semibold hover:bg-primaryHover transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-primary/30 flex items-center"
            >
              Industry Examples
              <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </NavLink>
          </div>
        </div>
      </section>

      <section id="secure-by-design" className="relative overflow-hidden bg-gradient-to-br from-ink-800 via-ink-900 to-ink-800 text-white py-16 sm:py-20 md:py-28 mb-0 max-w-full">
        <div className="absolute inset-0 bg-blueprint opacity-70" aria-hidden="true" />
        {/* Brand-gradient accent line ties the dark band to the identity above/below it. */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-accentPink opacity-80" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-primary/25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-accentPurple/25 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accentPink/10 blur-3xl" aria-hidden="true" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-5">
                Security isn't a feature.<br className="hidden sm:block" /> It's the <span className="text-accentPurple">foundation.</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed">
                We deploy private automation inside your environment and run it for you, so
                sensitive records never leave your control, and compliance stays intact.
              </p>
              <p className="text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed mt-4">
                That means the same access controls, audit trails, and data-residency rules your
                organization already operates under continue to apply — automation doesn't create a
                new, unmonitored path for sensitive information to travel through.
              </p>
              <button
                type="button"
                onClick={() => bookAssessment()}
                className="group mt-8 inline-flex items-center bg-white text-ink-900 px-6 py-3 rounded-full text-base font-semibold hover:bg-slate-100 transition-all duration-300"
              >
                Talk to our team
                <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Reveal>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
              {securityPillars.map((pillar, index) => (
                <Reveal
                  key={pillar.title}
                  delay={index * 0.08}
                  className="rounded-2xl border border-ink-line bg-ink-800/70 p-5 sm:p-6 transition-colors duration-300 hover:border-primary/60"
                >
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/15 border border-primary/30 text-[#6db6ec] mb-4">
                    <pillar.icon aria-hidden="true" className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{pillar.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{pillar.detail}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden max-w-full bg-surfaceSoft">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(174,72,142,0.16),transparent_42%),radial-gradient(circle_at_85%_92%,rgba(110,100,183,0.14),transparent_42%)]" />
          <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-accentPink/10 blur-3xl" />
          <div className="absolute -bottom-24 left-0 h-96 w-96 rounded-full bg-accentPurple/10 blur-3xl" />
        </div>

        <section id="differences" className="relative z-10 py-12 sm:py-16 md:py-24 mb-0 max-w-full">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary mb-3 sm:mb-4">
                Why Choose Contextral
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto px-2">
                Applying new technology responsibly is hard. Our experts make it secure and worthwhile.
              </p>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {whyChoosePillars.map((item, index) => (
                <Reveal key={index} delay={index * 0.08} className="group bg-gradient-to-b from-surface to-surfaceSoft rounded-2xl p-8 border border-borderSubtle shadow-sm transition-all duration-300 hover:border-primary hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/10">
                  <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 shadow-lg shadow-primary/20">
                    <item.icon aria-hidden="true" className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-3 transition-colors duration-300 group-hover:text-primary">{item.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{item.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="call-to-action" className="relative z-10 py-12 sm:py-16 md:py-24 mb-0 max-w-full">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden bg-primary rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 text-center shadow-xl shadow-primary/25">
              <div className="absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">Ready to See What Automation Can Do for You?</h2>
                <p className="text-base sm:text-lg md:text-xl text-surfaceSoft mb-6 sm:mb-8">
                  Book a free automation assessment and we'll map where our services can help save you the most time and risk.
                </p>
                <button
                  type="button"
                  onClick={() => bookAssessment()}
                  className="group w-full sm:w-auto inline-flex items-center justify-center bg-surface text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg"
                >
                  Book a Free Automation Assessment
                  <ArrowRight aria-hidden="true" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  to="/catalog"
                  className="mt-3 sm:mt-0 sm:ml-3 w-full sm:w-auto inline-flex items-center justify-center rounded-full border border-white/70 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  Browse ready-made applications
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
